// The sign-in flow as a store rather than a component, so the web views and a
// future native app render the same states and call the same actions.

import { createStore, type StoreApi } from "zustand/vanilla";

import { AuthError, type AuthErrorCode, type AuthSource, type AuthUser } from "./source.ts";

export const CODE_LENGTH = 6;
export const RESEND_COOLDOWN_MS = 60_000;

export type SignInMethod = "google" | "code";

export type FlowStep =
	| { k: "entry" }
	| { k: "code"; email: string; userId: string }
	/** Guest only: an existing account (named, if we know its email) would replace the guest. */
	| { k: "leave"; email: string | null }
	| { k: "done"; user: AuthUser; method: SignInMethod };

export interface FlowState {
	step: FlowStep;
	busy: boolean;
	err: AuthErrorCode | null;
	/** Epoch ms before which "resend" does nothing. */
	resendAt: number;
	/** The last address taken, so going back to the entry step keeps it. */
	email: string;
}

export interface FlowActions {
	submitEmail(email: string): Promise<void>;
	submitCode(code: string): Promise<void>;
	resend(): Promise<void>;
	changeEmail(): void;
	google(urls: { success: string; failure: string }): Promise<void>;
	haveAccount(): void;
	/** The browser restored this page from its back/forward cache: nothing is in flight any more. */
	pageRestored(): void;
}

export type FlowModel = FlowState & FlowActions;
export type Flow = StoreApi<FlowModel>;

export interface FlowOptions {
	auth: AuthSource;
	/** A guest saves by attaching the email, then entering the code. */
	guest: boolean;
	now?: () => number;
	cooldownMs?: number;
}

// Deliberately loose: the server is the real validator; this only catches
// typos before a round trip.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The code in whatever was typed, pasted or autofilled. A standalone group of
 * six digits (spaced or dashed in the middle) wins over the raw digits, so
 * "Your code: 123 456, valid until 2026" gives 123456, not 123456 run into 2026.
 */
export function extractCode(text: string): string {
	const group = /(?<!\d)(\d{3})[ -]?(\d{3})(?!\d)/.exec(text);
	if (group) return `${group[1]}${group[2]}`;
	return text.replace(/\D/g, "").slice(0, CODE_LENGTH);
}

const codeOf = (e: unknown): AuthErrorCode => (e instanceof AuthError ? e.code : "unknown");

export function createAuthFlow(o: FlowOptions): Flow {
	const now = o.now ?? Date.now;
	const cooldown = o.cooldownMs ?? RESEND_COOLDOWN_MS;

	return createStore<FlowModel>()((set, get) => {
		/** At most one call in flight, and every rejection becomes one closed code. */
		const run = async (work: () => Promise<void>): Promise<void> => {
			if (get().busy) return;
			set({ busy: true, err: null });
			try {
				await work();
			} catch (e) {
				set({ err: codeOf(e) });
			} finally {
				set({ busy: false });
			}
		};

		const send = async (email: string): Promise<void> => {
			const { userId } = await o.auth.sendCode(email);
			set({ step: { k: "code", email, userId }, resendAt: now() + cooldown });
		};

		return {
			step: { k: "entry" },
			busy: false,
			err: null,
			resendAt: 0,
			email: "",

			async submitEmail(raw) {
				const email = raw.trim();
				if (!EMAIL.test(email)) {
					set({ err: "invalidEmail" });
					return;
				}
				set({ email });
				if (!o.guest) {
					await run(() => send(email));
					return;
				}
				await run(async () => {
					try {
						const { userId } = await o.auth.attachEmail(email);
						set({ step: { k: "code", email, userId }, resendAt: now() + cooldown });
					} catch (e) {
						// Not an error to show: the address belongs to an account the
						// guest may simply want to sign in to.
						if (codeOf(e) !== "emailTaken") throw e;
						set({ step: { k: "leave", email } });
					}
				});
			},

			async submitCode(raw) {
				const s = get().step;
				if (s.k !== "code") return;
				const code = extractCode(raw);
				if (code.length !== CODE_LENGTH) {
					set({ err: "invalidToken" });
					return;
				}
				await run(async () => {
					const user = await o.auth.verifyCode(s.userId, code);
					set({ step: { k: "done", user, method: "code" } });
				});
			},

			async resend() {
				const s = get().step;
				if (s.k !== "code" || now() < get().resendAt) return;
				await run(() => send(s.email));
			},

			changeEmail() {
				set({ step: { k: "entry" }, err: null });
			},

			async google(urls) {
				if (get().busy) return;
				set({ busy: true, err: null });
				try {
					await o.auth.createOAuth2Session("Google", urls.success, urls.failure);
					// Stays busy: the browser is navigating to Google.
				} catch (e) {
					set({ busy: false, err: codeOf(e) });
				}
			},

			haveAccount() {
				if (o.guest) set({ step: { k: "leave", email: null }, err: null });
			},

			pageRestored() {
				set({ busy: false });
			},
		};
	});
}
